import { useSnackbar } from 'notistack';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AsyncBackdropSubmit from '../../../components/AsyncBackdropSubmit';
import TitlePage from '../../../components/layout/Page/TitlePage';
import FormSkeleton from '../../../components/Skeletons/FormSkeleton';
import { Form, Formik } from 'formik';
import { initialValuesEditDocument } from '../Registrations/types';
import { editDocumentionValidation } from '../Registrations/validationSchema';
import FormStepperBuilder from '../../../components/form_v2/highLevelComponents/FormStepperBuilder';
import { fetchOAtypeFormat, fetchTekmirioTypesOptions, getDocument } from '../../../lib/fetchers';
import HttpService from '../../../services/HttpService';
import { eliminateOjectNotTruthyValues, isSubmitEnabled } from '../../../lib/helperFuncs';
import { insertDocumentFields } from './static';
import FormActionButtonLayout from '../../../components/layout/Form/FormActionButtonLayout';
import { Button } from '@mui/material';
import PreviewTekmrio from './PreviewTekmrio';
import { buildFilesRequest } from '../Registrations/RegistrationDocument';

const EditTekmirio = () => {
  document.title = 'Επεξεργασία Τεκμηρίου';

  const [omekaResponse, setOmekaResponse] = React.useState<any>({});
  const [isLoadingDocument, setIsLoadingDocument] = React.useState(true);
  const [renderPreview, setRenderPreview] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState<boolean>(false);
  const [collections, setCollections] = React.useState<any[]>([]);

  const [typeOptions, setTypeOptions] = React.useState<any[]>([]);
  const [formatOptions, setFormatOptions] = React.useState<any[]>([]);
  const [erga, setErga] = React.useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const onSubmitEdit = async (values: any) => {
    setOpenBackdrop(true);
    try {
      const x = collections.find(collection => collection?.key.trim() === values['documentsRelationCollection'].trim())?.['collection_id'];
      
      const _documentsAttachedMediaImages = values.documentsAttachedMediaImages;
      const _documentsAttachedMediaFiles = values.documentsAttachedMediaFiles;
      await HttpService.getAxiosClient().put(
        `${process.env.REACT_APP_BACKEND_URL}/tekmirio/${omekaResponse.id}`,
        eliminateOjectNotTruthyValues({ 
          ...values,
          id: Number(values.id),
          documentsUniqueCode: Number(values.documentsUniqueCode),
          documentsID: Number(values.documentsID),
          documentsAttachedMediaImages: values.documentsAttachedMediaImages.filter((x: any) =>typeof x ==='string'),
          documentsAttachedMediaFiles: values.documentsAttachedMediaFiles.filter((x: any) =>typeof x ==='string'),
          documentsRelationCollection: [{ key: x, value: x}],
          documentsRelationProject: [{ 
            key: values?.documentsRelationProject, 
            value: values?.documentsRelationProject
          }]
        })
      );
      if (values?.documentsAttachedMediaImages || values?.documentsAttachedMediaFiles) {
        const document_formData = buildFilesRequest(
          _documentsAttachedMediaImages,
          _documentsAttachedMediaFiles,
        );
        await HttpService.getAxiosClient().post(`${process.env.REACT_APP_BACKEND_URL}/tekmirio/files/${omekaResponse.id}`, document_formData);
      }
      enqueueSnackbar('H καταχώριση τεκμηρίου υποβλήθηκε επιτυχώς', {
        variant: 'success',
      });
      navigate('/admin/document/dashboard');
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        'H καταχώριση τεκμηρίου απέτυχε, παρακαλώ προσπαθήστε ξανά.',
        { variant: 'error' }
      );
    } finally {
      setOpenBackdrop(false);
    }
  };

  const getTypeOptions = async () => {
    try {
      const response = await fetchTekmirioTypesOptions();
      const _tekmirioTypeOptions = response?.['o:terms']
        .map((x:any) => {
          return {
            key: x, 
            value: x
          };
        })
        .sort((a: any,b: any) => a?.key.localeCompare(b?.key, 'el'));
      setTypeOptions(_tekmirioTypeOptions);
    } catch (error) {
      console.error(error);
    }
  };

  const getCollections = async () => {
    try {
      const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/collections`);
      const collectionsResults = resp.data?.data?.map((x: any) => {
        return {
          key: x['collections:collectionsTitle'][0]?.['@value'], 
          value: x['collections:collectionsUniqueCode'][0]?.['@value'],
          collectionsIdentifierUniqueCode: x['collections:collectionsIdentifierUniqueCode'][0]?.['@value'],
          collection_id: x['o:id']
        };
      });      
      setCollections(collectionsResults);
    } catch (error) {
      console.error(error);
    }
  };

  const getTypeFormat = async () => {
    try {
      const response = await fetchOAtypeFormat();
      const _tekmirioFormatOptions = response?.['o:terms']
        .map((x:any) => {
          return {
            key: x, 
            value: x
          };
        })
        .sort((a: any,b: any) => a?.key?.localeCompare(b?.key, 'el'));
      setFormatOptions(_tekmirioFormatOptions);
    } catch (error) {
      console.error(error);
    }
  };
  const getErga = async () => {
    const response = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/ergo`);
    setErga(response.data?.data?.map((ergo: any) => {
      return {
        key: ergo['o:title'],
        value: ergo['o:id']
      };
    }));
  };

  const fetchDocument = async (document_id: string) => {
    try {
      console.log(document_id);
      
      setIsLoadingDocument(true);
      const { 
        document: document_object
      } = await getDocument(document_id);
      setOmekaResponse({
        ...document_object
      });

    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  React.useEffect(() => {
    searchParams.delete('edit');
    searchParams.delete('preview');
    setSearchParams(searchParams);

    id && fetchDocument(id);

    Promise.all([
      getTypeOptions(),
      getCollections(),
      getTypeFormat(),
      getErga()
    ]);

  }, []);

  // Set confirmation message in case someone wants to refresh or leave the page
  React.useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, []);

  return (
    <>
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...
      </AsyncBackdropSubmit>
      <TitlePage
        backButton={false}
        title={
          Object.prototype.hasOwnProperty.call(
            Object.fromEntries([...searchParams]),
            'preview'
          )
            ? 'Προεπισκόπηση Επεξεργασίας Τεκμηρίου'
            : 'Επεξεργασία Τεκμηρίου'
        }
      />
      {isLoadingDocument
        ? (
          <FormSkeleton lines={6} />
        )
        : !renderPreview
          ? (
            <>
              <Formik
                enableReinitialize
                onSubmit={() => undefined}
                initialValues={initialValuesEditDocument(omekaResponse)}
                validationSchema={editDocumentionValidation}
              >
                {(formik) => (
                  <>
                    <Form>
                      <FormStepperBuilder
                        formik={formik}
                        form={insertDocumentFields(typeOptions, formatOptions, erga)}
                      />
                      <FormActionButtonLayout>
                        <Button
                          variant='outlined'
                          color='error'
                          onClick={() => navigate('/admin/document/dashboard')}
                        >
                          Ακύρωση Επεξεργασίας
                        </Button>
                        <Button
                          variant='contained'
                          disabled={isSubmitEnabled(formik?.errors, true)}
                          onClick={() => {
                            setOmekaResponse({ ...formik.values } as any);
                            searchParams.set('preview', 'true');
                            searchParams.delete('edit');
                            setSearchParams(searchParams);
                            setRenderPreview(true);
                          }}
                        >
                          Προεπισκόπηση Καταχώρισης
                        </Button>
                      </FormActionButtonLayout>
                    </Form>
                  </>
                )}
              </Formik>
            </>
          )
          : (
            <PreviewTekmrio
              onSubmit={onSubmitEdit}
              data={omekaResponse}
              setRenderPreview={setRenderPreview}
              isLoading={isLoadingDocument}
              erga={erga}
            />
          )}
    </>
  );
};

export default EditTekmirio;